import { Component, ViewChild } from '@angular/core';
import { BasePageComponent } from '@m4u-angular/base/component/BasePageComponent';
import { ServiceBagService, ServiceBagServices } from '@m4u-angular/base/component/ServiceBag';
import { DataSourceCustomAction } from '@m4u-angular/base/datalayer/BaseDataSources';
import { DataLoadOptions } from '@m4u-angular/base/datalayer/DataLoadOptions';
import {
    BaseState, CreateCredentialsParams, CredentialsCheckerMetadata,
    CredentialExternalInfo, CredentialType, SystemCredentialParams, SystemCredentialInfo, GenericListEntity, BaseEntitiesResponse
}
    from '@m4u-angular/base/entities/BaseEntities';
import { CredentialActivityEndpoint } from '@m4u-angular/core/endpoints/credential-activity.endpoint';
import { SystemCredentialsEndpoint } from '@m4u-angular/core/endpoints/system-credentials.endpoint';
import { EnumSystemOptions, EnumYesNo } from '@m4u-angular/core/framework/sysenums.enum';
import { WelcomeMessageConfigDialogComponent }
    from '@m4u-angular/ui/dialogs/welcome-message-config-dialog/welcome-message-config-dialog.component';
import { SystemCredentialsGridComponent } from './system-credentials-grid/system-credentials-grid.component';
import { ToolbarSettings } from '@m4u-angular/base/classes/BaseClasses';

export class State extends BaseState {
    CredentialTypes: CredentialType[];
    AdditionalInfoMetadata: CredentialsCheckerMetadata;
    SelectedRows: CredentialExternalInfo[] = [];
    CredentialType: number;
    CredentialID: number;
    IsActive: any;
    IsPendingWelcome: any;
    IsLockedOut: any;
    CredentialFilters: GenericListEntity[] = []
    CredentialFilter: number;
    IsSupportedUserWelcome: boolean = false;
}

@Component({
    providers: ServiceBagServices,
    selector: 'app-system-credentials',
    templateUrl: './system-credentials.component.html',
    styleUrls: ['./system-credentials.component.scss']
})
export class SystemCredentialsComponent extends BasePageComponent<SystemCredentialInfo, State> {
    @ViewChild(WelcomeMessageConfigDialogComponent) dialog: WelcomeMessageConfigDialogComponent;
    @ViewChild(SystemCredentialsGridComponent) grid: SystemCredentialsGridComponent;

    get IsAllowPagination() : boolean {
        return !this.State.CredentialFilter;
    }

    constructor(A: ServiceBagService,
        private credentialActivityEndpoint: CredentialActivityEndpoint,
        private systemCredentialEndpoint: SystemCredentialsEndpoint) {
        super(A, 'SystemCredentialsComponent', SystemCredentialInfo, State);
    }

    protected override OnInitComponent() {
        this.InitState();
        this.GetParamsFromQuery();
        this.LoadDataSource();
        this.Do(this.credentialActivityEndpoint.ListCredentialTypes(), false, (response) => {
            this.State.CredentialTypes = response.Entities;
        });
        this.Do(this.systemCredentialEndpoint.GetAdditionalInfoMetadata(), false, (response) => {
            this.State.AdditionalInfoMetadata = response.Data;
        });

        this.Do(
            this.systemCredentialEndpoint.GetAvailableCredentialFilters(), 
            false, 
            (response: BaseEntitiesResponse<GenericListEntity>) => {
                this.State.CredentialFilters = response.Entities ?? [];
            }
        );
    }
    
    override InitState() {
        super.InitState();
        this.State.IsSupportedUserWelcome = this.A.Framework.SystemOptionFlag(EnumSystemOptions.IsSupportedUserWelcome);
    }

    override OnGetToolbarSettings() {
        const settings = new ToolbarSettings;
        settings.IsAllowFilter = true;
        settings.GridExtender = this.grid?.G;
        settings.IsAllowExport = true;
        return settings;
    }

    protected override OnApplyFilter() {
        this.grid.G.gridAny?.instance?.getScrollable()?.scrollTo({ top: 0 });
        this.LoadDataSource();
    }

    protected override OnClearFilter() {
        this.State.CredentialType = undefined;
        this.State.CredentialID = undefined;
        this.State.IsActive = undefined;
        this.State.IsPendingWelcome = undefined;
        this.State.IsLockedOut = undefined;
        this.State.CredentialFilter = undefined;
        this.LoadDataSource();
    }

    private GetParamsFromQuery() {
        if (this.QueryParams.CredentialID) {
            this.State.CredentialID = +this.QueryParams.CredentialID;
        }
    }
    private LoadDataSource() {
        this.DataSources['SystemCredentials'] = new DataSourceCustomAction((options: DataLoadOptions) => {
            const request = new SystemCredentialParams();
            request.CredentialType = this.State.CredentialType;
            request.CredentialID = this.State.CredentialID;
            request.IsActive = this.State.IsActive === EnumYesNo.Yes ?
                true : this.State.IsActive === EnumYesNo.No ? false : undefined;
            request.IsPendingWelcome = this.State.IsPendingWelcome === EnumYesNo.Yes ?
                true : this.State.IsPendingWelcome === EnumYesNo.No ? false : undefined;
            request.IsLockedOut = this.State.IsLockedOut === EnumYesNo.Yes ?
                true : this.State.IsLockedOut === EnumYesNo.No ? false : undefined;
            request.CredentialFilter = this.State.CredentialFilter;
            
            return this.systemCredentialEndpoint.ListSystemCredentials(request, options);
        }, undefined, undefined, !this.IsAllowPagination);

        this.DS['SystemCredentials']?.Reload();
    }

    OnSelectionChanged(event) {
        this.State.SelectedRows = event;
    }

    ShowMessageConfigDialog() {
        this.dialog.ShowDialogShort((data) => {
            const { NotificationDeliveryOption, LanguageID } = data;
            const params = new CreateCredentialsParams();
            params.Language = LanguageID;
            params.DeliveryOption = NotificationDeliveryOption;
            params.CredentialsForCreation = this.State.SelectedRows;
            this.Do(this.systemCredentialEndpoint.SendWelcomeUserMessage(params), false, (data) => {
                this.ReloadComponent();
                this.UI.InfoMessage(`${this.RC('msgSuccess')}: ${data.Data} ${this.RC('lblTimes')}`);
            });
        });
    }

    OnRefresh() {
        this.LoadDataSource();
    }
}
